import { useCallback, useEffect, useState } from 'react';
import { useApi } from '../../../../hooks/useApi';
import { fmtDateTime } from '../../../../utils/ruDates';
import { createFeedbackReport, estimateFeedbackReport, getFeedbackReport, listFeedbackReports } from '../../../../services/feedback';
import { Stars, errorText, money } from './feedbackUi';

const STATUS = { queued: 'В очереди', running: 'Считается', done: 'Готов', failed: 'Не удался' };
const FAIL_REASONS = { interrupted: 'расчёт оборвался (перезапуск сервера)', insufficient_balance: 'не хватило денег на балансе плательщика' };
const STAR_COLORS = { 1: '#d64545', 2: '#e8833a', 3: '#e3b341', 4: '#7bb662', 5: '#3f9b4f' };

const dateRu = (iso) => (iso ? iso.split('-').reverse().join('.') : '');
const pct = (value) => `${Number(value || 0).toFixed(1).replace('.0', '')}%`;
const delta = (now, before, suffix = '') => {
    if (now === null || now === undefined || before === null || before === undefined) return null;
    const diff = Number(now) - Number(before);
    if (Math.abs(diff) < 0.005) return '0';
    return `${diff > 0 ? '+' : ''}${diff.toFixed(Math.abs(diff) < 10 ? 2 : 1).replace(/\.?0+$/, '')}${suffix}`;
};

// Столбцы «отзывов за период» и линия средней оценки поверх них.
function ReviewsChart({ series }) {
    if (!series.length) return null;
    const width = 640;
    const height = 200;
    const pad = { left: 36, right: 36, top: 14, bottom: 34 };
    const max = Math.max(...series.map((point) => point.reviews), 1);
    const step = (width - pad.left - pad.right) / series.length;
    const barWidth = Math.max(Math.min(step * 0.6, 40), 4);
    const y = (value) => pad.top + (height - pad.top - pad.bottom) * (1 - value / max);
    const yRating = (value) => pad.top + (height - pad.top - pad.bottom) * (1 - (value - 1) / 4);
    const points = series.filter((point) => point.average).map((point) => (
        `${pad.left + step * series.indexOf(point) + step / 2},${yRating(point.average)}`));
    return (
        <svg viewBox={`0 0 ${width} ${height}`} style={{ width: '100%', maxWidth: width, height: 'auto' }} role="img" aria-label="Отзывы по периодам и средняя оценка">
            {[0, 0.5, 1].map((part) => (
                <g key={part}>
                    <line x1={pad.left} x2={width - pad.right} y1={y(max * part)} y2={y(max * part)} stroke="currentColor" opacity="0.12" />
                    <text x={pad.left - 6} y={y(max * part) + 4} fontSize="10" textAnchor="end" fill="currentColor" opacity="0.6">{Math.round(max * part)}</text>
                </g>
            ))}
            {[1, 3, 5].map((value) => (
                <text key={value} x={width - pad.right + 6} y={yRating(value) + 4} fontSize="10" fill="#e3a21a">{value}★</text>
            ))}
            {series.map((point, index) => {
                const x = pad.left + step * index + (step - barWidth) / 2;
                return (
                    <g key={point.period}>
                        <rect x={x} y={y(point.reviews)} width={barWidth} height={height - pad.bottom - y(point.reviews)} fill="#77baf0" rx="2">
                            <title>{`${dateRu(point.period)}: отзывов ${point.reviews}, средняя ${point.average ?? '—'}`}</title>
                        </rect>
                        {(series.length <= 14 || index % Math.ceil(series.length / 12) === 0) && (
                            <text x={x + barWidth / 2} y={height - 14} fontSize="9" textAnchor="middle" fill="currentColor" opacity="0.65">{dateRu(point.period).slice(0, 5)}</text>
                        )}
                    </g>
                );
            })}
            {points.length > 1 && <polyline points={points.join(' ')} fill="none" stroke="#e3a21a" strokeWidth="2" />}
            {points.map((point, index) => <circle key={index} cx={point.split(',')[0]} cy={point.split(',')[1]} r="3" fill="#e3a21a" />)}
        </svg>
    );
}

// Две линии в процентах: доля отзывов с текстом и доля негатива (1–3★).
function ShareChart({ series }) {
    const data = series.filter((point) => point.reviews > 0);
    if (data.length < 2) return <div className="fb-muted">Для графика нужно хотя бы два периода с отзывами.</div>;
    const width = 640;
    const height = 180;
    const pad = { left: 36, right: 16, top: 14, bottom: 30 };
    const x = (index) => pad.left + ((width - pad.left - pad.right) * index) / (data.length - 1);
    const y = (value) => pad.top + (height - pad.top - pad.bottom) * (1 - Math.min(value, 100) / 100);
    const line = (picker) => data.map((point, index) => `${x(index)},${y(picker(point))}`).join(' ');
    return (
        <svg viewBox={`0 0 ${width} ${height}`} style={{ width: '100%', maxWidth: width, height: 'auto' }} role="img" aria-label="Доля отзывов с текстом и негатива по периодам">
            {[0, 50, 100].map((value) => (
                <g key={value}>
                    <line x1={pad.left} x2={width - pad.right} y1={y(value)} y2={y(value)} stroke="currentColor" opacity="0.12" />
                    <text x={pad.left - 6} y={y(value) + 4} fontSize="10" textAnchor="end" fill="currentColor" opacity="0.6">{value}%</text>
                </g>
            ))}
            <polyline points={line((point) => (point.with_text / point.reviews) * 100)} fill="none" stroke="#77baf0" strokeWidth="2" />
            <polyline points={line((point) => (point.negative / point.reviews) * 100)} fill="none" stroke="#f15555" strokeWidth="2" />
            {data.map((point, index) => (
                <g key={point.period}>
                    <circle cx={x(index)} cy={y((point.with_text / point.reviews) * 100)} r="3" fill="#77baf0"><title>{`${dateRu(point.period)}: с текстом ${pct((point.with_text / point.reviews) * 100)}`}</title></circle>
                    <circle cx={x(index)} cy={y((point.negative / point.reviews) * 100)} r="3" fill="#f15555"><title>{`${dateRu(point.period)}: негатив ${pct((point.negative / point.reviews) * 100)}`}</title></circle>
                    {(data.length <= 12 || index % Math.ceil(data.length / 10) === 0) && (
                        <text x={x(index)} y={height - 10} fontSize="9" textAnchor="middle" fill="currentColor" opacity="0.65">{dateRu(point.period).slice(0, 5)}</text>
                    )}
                </g>
            ))}
            <g fontSize="11">
                <circle cx={pad.left + 4} cy={6} r="4" fill="#77baf0" /><text x={pad.left + 12} y={9} fill="currentColor">с текстом</text>
                <circle cx={pad.left + 90} cy={6} r="4" fill="#f15555" /><text x={pad.left + 98} y={9} fill="currentColor">негатив 1–3★</text>
            </g>
        </svg>
    );
}

function DistributionBar({ distribution, total }) {
    if (!total) return null;
    return (
        <div>
            <div style={{ display: 'flex', height: 18, borderRadius: 9, overflow: 'hidden', background: 'var(--surface-border)' }}>
                {[5, 4, 3, 2, 1].map((star) => {
                    const count = distribution?.[String(star)] || 0;
                    return count ? <div key={star} style={{ width: `${(count / total) * 100}%`, background: STAR_COLORS[star] }} title={`${star}★: ${count}`} /> : null;
                })}
            </div>
            <div className="fb-row" style={{ gap: 12, marginTop: 4 }}>
                {[5, 4, 3, 2, 1].map((star) => (
                    <span key={star} className="fb-note"><span style={{ color: STAR_COLORS[star] }}>●</span> {star}★ {distribution?.[String(star)] || 0}</span>
                ))}
            </div>
        </div>
    );
}

function Compare({ label, now, before, suffix = '', betterWhenLower = false }) {
    const change = delta(now, before, suffix);
    const diff = Number(now) - Number(before);
    const good = change && change !== '0' && (betterWhenLower ? diff < 0 : diff > 0);
    const bad = change && change !== '0' && !good;
    return (
        <div className="fb-stat">
            <b>{now === null || now === undefined ? '—' : `${now}${suffix}`}</b>
            <span>{label}</span>
            {change && <div style={{ fontSize: 12, color: good ? 'var(--text-success)' : bad ? 'var(--accent-warm)' : 'var(--text-muted)' }}>{change} к предыдущему периоду</div>}
        </div>
    );
}

function ProductBlock({ item }) {
    const analysis = item.analysis;
    const points = (title, list) => (list && list.length ? (
        <div><b>{title}</b><ul className="fb-points">{list.map((entry, index) => <li key={index}>{entry.text}{entry.count ? ` · ${entry.count}` : ''}</li>)}</ul></div>
    ) : null);
    return (
        <div className="fb-card" style={{ display: 'flex', flexDirection: 'column', gap: 8, breakInside: 'avoid' }}>
            <div className="fb-row fb-spread">
                <div>
                    <b>{item.name || 'Товар'}</b>
                    <div className="fb-item__meta">SKU {item.sku}{item.article ? ` · ${item.article}` : ''}</div>
                </div>
                <div className="fb-row" style={{ gap: 14 }}>
                    <span>★ {item.average_rating ?? '—'}</span>
                    <span className="fb-note">отзывов {item.reviews}</span>
                    <span className="fb-note">с текстом {item.with_text} ({pct(item.reviews ? (item.with_text / item.reviews) * 100 : 0)})</span>
                    <span className="fb-note">позитив {item.positive_text}, негатив {item.negative_text} (с текстом)</span>
                </div>
            </div>
            <DistributionBar distribution={item.distribution} total={item.reviews} />
            {analysis ? (
                <>
                    {analysis.summary && <div>{analysis.summary}</div>}
                    <div className="fb-two">
                        {points('Что нравится', analysis.advantages)}
                        {points('Что не нравится', analysis.disadvantages)}
                    </div>
                    {points('Вероятные причины', analysis.causes)}
                    {analysis.recommendations?.length > 0 && <div><b>Что сделать</b><ul className="fb-points">{analysis.recommendations.map((entry, index) => <li key={index}>{entry}</li>)}</ul></div>}
                </>
            ) : <div className="fb-muted">{item.analysis_error ? 'Разбор не удался.' : 'ИИ-разбор не делался: отзывов с текстом меньше трёх или артикул вне первых 60 по числу текстов.'}</div>}
            {item.quotes && (item.quotes.negative?.length > 0 || item.quotes.positive?.length > 0) && (
                <details>
                    <summary className="fb-note" style={{ cursor: 'pointer' }}>Примеры отзывов</summary>
                    {[...(item.quotes.negative || []), ...(item.quotes.positive || [])].map((quote, index) => (
                        <div key={index} className="fb-item__text" style={{ marginTop: 6 }}><Stars rating={quote.rating} /> {quote.text}</div>
                    ))}
                </details>
            )}
        </div>
    );
}

function ReportView({ report, onBack }) {
    const data = report.result;
    if (!data) return <div className="fb-card fb-muted">Содержимое отчёта ещё не готово.</div>;
    const { totals, previous, series, products, overall } = data;
    const periodLabel = `${dateRu(data.period.from)} – ${dateRu(data.period.to)}`;
    return (
        <div className="fb-report" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div className="fb-row fb-spread">
                <div>
                    <h3 style={{ margin: 0 }}>Отчёт по отзывам</h3>
                    <div className="fb-muted">{periodLabel} · {totals.products} артикулов · {report.params.skus ? 'выбранные артикулы' : 'весь ассортимент'} · сформирован {fmtDateTime(data.generated_at)}</div>
                </div>
                <div className="fb-row no-print">
                    <button type="button" onClick={() => window.print()}>Печать / PDF</button>
                    <button type="button" onClick={onBack}>К списку отчётов</button>
                </div>
            </div>

            <div className="fb-stats">
                <Compare label="отзывов" now={totals.reviews} before={previous.reviews} />
                <Compare label="средняя оценка" now={totals.average} before={previous.average} />
                <Compare label="с текстом" now={Number(totals.text_share.toFixed(1))} before={previous.text_share} suffix="%" />
                <Compare label="негатив 1–3★" now={Number(totals.negative_share.toFixed(1))} before={previous.negative_share} suffix="%" betterWhenLower />
                <div className="fb-stat"><b>{totals.positive_text}</b><span>позитивных с текстом</span></div>
                <div className="fb-stat"><b>{totals.negative_text}</b><span>негативных с текстом</span></div>
            </div>
            <div className="fb-note">Предыдущий период для сравнения: {dateRu(previous.from)} – {dateRu(previous.to)}, отзывов {previous.reviews}.</div>

            {overall ? (
                <div className="fb-card" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <h4 style={{ margin: 0 }}>Выводы</h4>
                    <div>{overall.summary}</div>
                    {overall.dynamics && <div><b>Динамика.</b> {overall.dynamics}</div>}
                    {overall.strengths?.length > 0 && <div><b>Что получается</b><ul className="fb-points">{overall.strengths.map((entry, index) => <li key={index}>{entry}</li>)}</ul></div>}
                    {overall.problems?.length > 0 && (
                        <div>
                            <b>Проблемы</b>
                            <ul className="fb-points">
                                {overall.problems.map((entry, index) => (
                                    <li key={index}><b>{entry.title}</b>{entry.sku ? ` (SKU ${entry.sku})` : ''}: {entry.why}{entry.action ? ` → ${entry.action}` : ''}</li>
                                ))}
                            </ul>
                        </div>
                    )}
                    {overall.actions?.length > 0 && <div><b>Что делать</b><ol className="fb-points">{overall.actions.map((entry, index) => <li key={index}>{entry}</li>)}</ol></div>}
                </div>
            ) : <div className="fb-card fb-muted">Итоговый вывод не получился{data.overall_error ? ` (${data.overall_error})` : ''}: цифры и разбор артикулов ниже готовы.</div>}

            <div className="fb-two">
                <div className="fb-card" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <h4 style={{ margin: 0 }}>Отзывы и средняя оценка по {data.granularity === 'week' ? 'неделям' : 'месяцам'}</h4>
                    <ReviewsChart series={series} />
                </div>
                <div className="fb-card" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <h4 style={{ margin: 0 }}>Доля текстовых отзывов и негатива</h4>
                    <ShareChart series={series} />
                </div>
            </div>
            <div className="fb-card" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <h4 style={{ margin: 0 }}>Распределение оценок</h4>
                <DistributionBar distribution={totals.distribution} total={totals.reviews} />
            </div>

            <h4 style={{ margin: '6px 0 0' }}>Артикулы ({products.length}), сначала с наибольшим негативом</h4>
            {products.map((item) => <ProductBlock key={item.sku} item={item} />)}
            <div className="fb-note">Списано за отчёт: {money(report.price_rub)}.</div>
        </div>
    );
}

// Выбор артикулов приходит из таблицы анализа: skus (пусто — весь ассортимент) и период.
export default function FeedbackReports({ accountId, skus, period, scopeLabel, periodReady, hasScope }) {
    const { request } = useApi();
    const [reports, setReports] = useState([]);
    const [estimate, setEstimate] = useState(null);
    const [busy, setBusy] = useState(false);
    const [message, setMessage] = useState('');
    const [open, setOpen] = useState(null);

    const reload = useCallback(async () => {
        try {
            setReports((await listFeedbackReports({ request, accountId })).items);
        } catch (err) {
            // список отчётов не критичен для остальной страницы
        }
    }, [request, accountId]);

    useEffect(() => { reload(); }, [reload]);
    useEffect(() => { setEstimate(null); setMessage(''); }, [skus, period]);
    useEffect(() => {
        if (!reports.some((item) => item.status === 'queued' || item.status === 'running')) return undefined;
        const timer = setTimeout(reload, 5000);
        return () => clearTimeout(timer);
    }, [reports, reload]);

    const runEstimate = async () => {
        setBusy(true);
        setMessage('');
        try {
            setEstimate(await estimateFeedbackReport({ request, accountId, skus, period }));
        } catch (err) {
            setMessage(errorText(err));
        } finally {
            setBusy(false);
        }
    };
    const create = async () => {
        setBusy(true);
        setMessage('');
        try {
            await createFeedbackReport({ request, accountId, skus, period });
            setEstimate(null);
            setMessage('Отчёт считается в фоне, он появится в списке ниже. Можно закрыть страницу.');
            await reload();
        } catch (err) {
            let text = errorText(err);
            try {
                const code = JSON.parse(err.message)?.code;
                if (code === 'report_running') text = 'Другой отчёт этого кабинета ещё считается: дождитесь его окончания';
                if (code === 'no_reviews') text = 'За выбранный период в выбранных артикулах нет отзывов';
            } catch (parseError) {
                // обычное сообщение
            }
            setMessage(text);
        } finally {
            setBusy(false);
        }
    };
    const show = async (item) => {
        setMessage('');
        try {
            setOpen(await getFeedbackReport({ request, reportId: item.id }));
        } catch (err) {
            setMessage(errorText(err));
        }
    };

    if (open) return <ReportView report={open} onBack={() => setOpen(null)} />;

    return (
        <div className="fb-card" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div className="fb-row fb-spread">
                <div>
                    <h4 style={{ margin: 0 }}>Отчёт по отзывам</h4>
                    <div className="fb-muted">Выбрано: {hasScope ? scopeLabel : 'ничего: отметьте артикулы галочками в таблице или «все» в заголовке'}. Отзывы за период, динамика к предыдущему периоду, графики, разбор каждого артикула и вывод по работе с качеством.</div>
                </div>
                <button type="button" className="primary" disabled={busy || !periodReady || !hasScope} onClick={runEstimate}>Оценить стоимость отчёта</button>
            </div>
            {estimate && (
                <div className="fb-card" style={{ display: 'flex', flexDirection: 'column', gap: 6, background: 'transparent' }}>
                    <div>
                        В отчёт войдёт <b>{estimate.products}</b> артикулов, <b>{estimate.reviews}</b> отзывов ({estimate.with_text} с текстом).
                        ИИ разберёт <b>{estimate.analyzed_products}</b> артикулов (где есть 3 и больше текстов) и сделает итоговый вывод: {estimate.llm_calls} запросов,
                        около {Math.round((estimate.input_tokens + estimate.output_tokens) / 1000)} тыс. токенов.
                    </div>
                    <div>Стоимость: <b>от {money(estimate.price_min_rub)} до {money(estimate.price_max_rub)}</b>, обычно около {money(estimate.price_rub)}. Списывается с баланса плательщика по факту каждого вызова ИИ.</div>
                    <div className="fb-note">{estimate.note}</div>
                    <div className="fb-row">
                        <button type="button" className="primary" disabled={busy || estimate.reviews === 0} onClick={create}>Сформировать отчёт</button>
                        <button type="button" onClick={() => setEstimate(null)}>Отмена</button>
                    </div>
                </div>
            )}
            {message && <div className="fb-note">{message}</div>}
            {reports.length > 0 && (
                <div className="fb-table-wrap">
                    <table className="fb-table">
                        <thead><tr><th>Создан</th><th>Что в отчёте</th><th>Состояние</th><th className="fb-num">Цена</th><th /></tr></thead>
                        <tbody>
                            {reports.map((item) => (
                                <tr key={item.id}>
                                    <td>{fmtDateTime(item.created_at)}</td>
                                    <td>
                                        {item.params.skus ? `${item.params.skus.length} артикулов` : 'Весь ассортимент'}
                                        {item.params.date_from ? `, ${dateRu(item.params.date_from)} – ${dateRu(item.params.date_to)}` : `, ${item.params.days} дн.`}
                                    </td>
                                    <td>
                                        {STATUS[item.status] || item.status}
                                        {item.status === 'running' && item.total_steps > 0 ? ` ${item.progress} из ${item.total_steps}` : ''}
                                        {item.status === 'failed' && item.error ? `: ${FAIL_REASONS[item.error] || item.error}` : ''}
                                    </td>
                                    <td className="fb-num">{item.price_rub && item.status === 'done' ? money(item.price_rub) : item.estimate ? `${money(item.estimate.price_min_rub ?? item.estimate.price_rub)} – ${money(item.estimate.price_max_rub ?? item.estimate.price_rub)}` : '—'}</td>
                                    <td>{item.status === 'done' && <button type="button" onClick={() => show(item)}>Открыть</button>}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
